import { ref, Ref, watch, computed, onUnmounted } from "vue";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { GL_DRAW_DRAWING_STYLES } from "@/constants/map.constants";
import { booleanValid } from "@turf/turf";

export type DrawingMode = "draw_point" | "draw_polygon" | "none";

interface MapDrawingOptions {
	initialCoordinates?: Ref<[number, number] | undefined>;
	initialMode?: DrawingMode;
}

export function useDrawingLayer(
	map: Ref<mapboxgl.Map | null>,
	options: MapDrawingOptions = {},
) {
	const { initialCoordinates, initialMode = "draw_point" } = options;

	const draw = ref<MapboxDraw | null>(null);
	const isDrawReady = ref(false);
	const currentMode = ref<DrawingMode>("draw_point");
	const currentFeatures = ref<GeoJSON.Feature[]>([]);

	function updateFeatures() {
		if (draw.value) {
			currentFeatures.value = [...draw.value.getAll().features];
		}
	}

	/**
	 * Only features that are complete/valid (filters out partial drawings in progress)
	 */
	const completeFeatures = computed(() =>
		currentFeatures.value.filter(booleanValid),
	);

	/**
	 * Whether there are any complete features drawn
	 */
	const hasCompleteFeatures = computed(
		() => completeFeatures.value.length > 0,
	);

	// Get features by type from reactive state
	const getFeaturesByType = (type: string) =>
		currentFeatures.value.filter((f) => f.geometry.type === type);

	// Show clear button when there are relevant features
	const showClear = computed(() => {
		const targetType =
			currentMode.value === "draw_point" ? "Point" : "Polygon";
		return getFeaturesByType(targetType).length > 0;
	});

	// Create point feature
	const createPointFeature = (coords: [number, number]) =>
		({
			type: "Feature" as const,
			geometry: {
				type: "Point" as const,
				coordinates: coords,
			},
			properties: {},
		}) as GeoJSON.Feature;

	// Handle map clicks for point mode
	function handleMapClick(
		e: mapboxgl.MapMouseEvent | { lngLat?: { lng: number; lat: number } },
	) {
		if (currentMode.value !== "draw_point") return;

		// Check for lngLat property (present in both mouse and touch events)
		if (!e.lngLat) return;

		// Single point mode: remove existing point and add new one
		const existingPoints = getFeaturesByType("Point");
		if (existingPoints.length > 0) {
			draw.value?.delete(existingPoints[0].id as string);
		}

		if (draw.value) {
			draw.value.add(createPointFeature([e.lngLat.lng, e.lngLat.lat]));
		}
		// Update reactive state immediately
		updateFeatures();
	}

	const setCursor = (cursor: string) => {
		if (map.value) {
			map.value.getCanvas().style.cursor = cursor;
		}
	};

	/**
	 * Helper to initialize draw on a map once it's ready.
	 * Handles the common pattern of checking if loaded or waiting for load event.
	 */
	function initializeDrawWhenReady(mapInstance: mapboxgl.Map) {
		if (mapInstance.loaded()) {
			initializeDraw();
		} else {
			mapInstance.once("load", initializeDraw);
		}
	}

	function initializeDraw() {
		if (!map.value || draw.value) return;

		draw.value = new MapboxDraw({
			displayControlsDefault: false,
			styles: GL_DRAW_DRAWING_STYLES,
			userProperties: true,
		});

		// conflict between community and official types
		// https://github.com/mapbox/mapbox-gl-draw/issues/1257
		map.value.addControl(draw.value as unknown as mapboxgl.IControl);

		// Add click and touchend handlers for single point mode
		map.value.on("click", handleMapClick);
		map.value.on("touchend", handleMapClick);
		map.value.on("draw.create", updateFeatures);
		map.value.on("draw.update", updateFeatures);
		map.value.on("draw.delete", updateFeatures);

		// Set initial mode based on options
		changeMode(initialMode);

		// Mark draw as ready
		isDrawReady.value = true;
	}

	function setPointMode() {
		if (!draw.value || !map.value) return;

		draw.value.deleteAll();
		currentMode.value = "draw_point";
		draw.value.changeMode("simple_select");
		setCursor("crosshair");
		// Add initial point for single point mode, using either provided coords or map center
		const coords =
			initialCoordinates?.value || map.value.getCenter().toArray();
		draw.value.add(createPointFeature(coords as [number, number]));
		updateFeatures();
	}

	function setPolygonMode() {
		if (!draw.value) return;

		draw.value.deleteAll();
		currentMode.value = "draw_polygon";
		draw.value.changeMode("draw_polygon");
		setCursor("");
		updateFeatures();
	}

	function setNoneMode() {
		if (!draw.value) return;

		draw.value.deleteAll();
		currentMode.value = "none";
		draw.value.changeMode("simple_select");
		setCursor("");
		updateFeatures();
	}

	function changeMode(mode: DrawingMode) {
		const modeHandlers = {
			draw_point: setPointMode,
			draw_polygon: setPolygonMode,
			none: setNoneMode,
		};
		modeHandlers[mode]();
	}

	function clearDrawings() {
		if (!draw.value || !map.value) return;

		draw.value.deleteAll();

		// Restore appropriate mode state after clearing
		if (currentMode.value === "draw_point") {
			// Re-add initial point using current coords or map center
			const coords =
				initialCoordinates?.value || map.value.getCenter().toArray();
			draw.value.add(createPointFeature(coords as [number, number]));
		} else if (currentMode.value === "draw_polygon") {
			draw.value.changeMode("draw_polygon");
		}
		updateFeatures();
	}

	function finalizePolygonIfDrawing() {
		if (currentMode.value === "draw_polygon" && draw.value) {
			draw.value.changeMode("simple_select");
			updateFeatures();
		}
	}

	function cleanup(mapInstance?: mapboxgl.Map | null) {
		// Use the provided map instance (for cleanup during map swaps),
		// or fall back to map.value (for normal cleanup)
		const targetMap = mapInstance ?? map.value;

		if (targetMap) {
			targetMap.off("click", handleMapClick);
			targetMap.off("touchend", handleMapClick);
			targetMap.off("draw.create", updateFeatures);
			targetMap.off("draw.update", updateFeatures);
			targetMap.off("draw.delete", updateFeatures);
		}

		// Remove draw control from map if both exist and map is valid
		if (draw.value && targetMap && !targetMap._removed) {
			targetMap.removeControl(draw.value as unknown as mapboxgl.IControl);
		}

		// Always clear the draw reference, even if map is gone
		if (draw.value) {
			draw.value = null;
		}

		isDrawReady.value = false;
		currentFeatures.value = [];
	}

	// Watch for map changes - handle initialization and cleanup
	watch(map, (newMap, oldMap) => {
		// Cleanup old map if it exists and is different from new map
		if (oldMap && newMap !== oldMap) {
			cleanup(oldMap);
		}

		// Initialize new map if it exists and is different from old
		if (newMap && newMap !== oldMap) {
			initializeDrawWhenReady(newMap);
		}
	});

	// Watch for coordinate updates to handle async loading
	watch(
		() => initialCoordinates?.value,
		(coords) => {
			if (coords && draw.value && currentMode.value === "draw_point") {
				// Update the point to the new coordinates
				draw.value.deleteAll();
				draw.value.add(createPointFeature(coords));
				updateFeatures();
			}
		},
	);

	// Add existing feature to draw control
	function addFeature(feature: GeoJSON.Feature) {
		if (!draw.value) return;
		draw.value.add(feature);
		updateFeatures();
	}

	// Set to edit mode (simple_select) to allow editing existing features
	function setEditMode() {
		if (!draw.value) return;
		draw.value.changeMode("simple_select");
		setCursor("");
		updateFeatures();
	}

	// Delete all features without changing mode
	function deleteAllFeatures() {
		if (!draw.value) return;
		draw.value.deleteAll();
		updateFeatures();
	}

	onUnmounted(cleanup);

	return {
		draw,
		currentFeatures,
		completeFeatures,
		hasCompleteFeatures,
		currentMode,
		showClear,
		clearDrawings,
		changeMode,
		finalizePolygonIfDrawing,
		addFeature,
		setEditMode,
		deleteAllFeatures,
		isDrawReady,
	};
}
